// src/pages/DashboardApp.tsx
import React from "react";
import Analyzer from "./Analyzer";
import UpgradeModal from "../components/UpgradeModal";
import AuthModal from "../components/AuthModal";
import { useAuth } from "../auth/AuthProvider";
import SavedJobs from "./SavedJobs";
import { supabase } from "../lib/supabaseClient";
import DashboardSidebar, {
  type DashboardView,
} from "../components/dashboard/DashboardSidebar";

import DashboardHeader from "../components/dashboard/DashboardHeader";
import DashboardHome from "../components/dashboard/DashboardHome";
import JobAnalyzerPage from "./JobAnalyzerPage";
import Applications from "./Applications";
import Settings from "./Settings";
import "../styles/Theme.css";
import {
  getUsage,
  canUse,
  track,
} from "../lib/plan";
// 
export type UserPlan = "free" | "premium";

type ThemeMode = "light" | "dark";

export type RecentAnalysis = {
  id: string;
  title: string;
  score: number;
  createdAt: string;
};

export default function DashboardApp() {
  const [showUpgrade, setShowUpgrade] = React.useState(false);
  const [showAuth, setShowAuth] = React.useState(false);
  const [profileOpen, setProfileOpen] = React.useState(false);
  const { user, accessToken, plan, signOut } = useAuth();
  const API = process.env.REACT_APP_API ?? "http://localhost:8000";
  
  const [activeView, setActiveView] =
  React.useState<DashboardView>("dashboard");

  const [initialWorkspace, setInitialWorkspace] = React.useState<
    "none" | "job-analyzer" | "resume-analyzer" | "cv-builder" | "cover-letter"
  >("none");

  const [uiLanguage, setUiLanguage] = React.useState<
    "en" | "sv" | "de" | "fr" | "es"
  >("en");

  const [themeMode, setThemeMode] = React.useState<ThemeMode>(() => {
    const saved = localStorage.getItem("jobtailor-theme");

    return saved === "dark" ? "dark" : "light";
  });

  React.useEffect(() => {
    localStorage.setItem("jobtailor-theme", themeMode);
  }, [themeMode]);

  // NEW: controls if About text is visible
  const [aboutOpen, setAboutOpen] = React.useState(false);
  const [analysesToday, setAnalysesToday] = React.useState(0);
  const [coverLettersCreated, setCoverLettersCreated] = React.useState(0);

  const [resumeScore, setResumeScore] = React.useState(0);
  const [atsMatch, setAtsMatch] = React.useState(0);

  const [recentAnalyses, setRecentAnalyses] =
    React.useState<RecentAnalysis[]>([]);

  const [cvsCreated, setCvsCreated] = React.useState(0);
  // NEW: collapse/expand Saved Jobs section
  const [savedOpen, setSavedOpen] = React.useState(false);

  const [showPrivacyModal, setShowPrivacyModal] = React.useState(false);
  const [showTermsModal, setShowTermsModal] = React.useState(false);
  const [legalOpen, setLegalOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);

  // Contact form state
  const [cName, setCName] = React.useState("");
  const [cEmail, setCEmail] = React.useState("");
  const [cMsg, setCMsg] = React.useState("");
  const [cSending, setCSending] = React.useState(false);
  const [cSent, setCSent] = React.useState<null | "ok" | "err">(null);
  
  const [contactExpanded, setContactExpanded] = React.useState(false);
  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null);
  const turnstileRef = React.useRef<HTMLDivElement | null>(null);

  const [atsCvText, setAtsCvText] = React.useState("");
  const [atsJobDescription, setAtsJobDescription] = React.useState("");
  const [atsLoading, setAtsLoading] = React.useState(false);
  const [atsError, setAtsError] = React.useState("");
  const [atsReport, setAtsReport] = React.useState<any>(null);

  const siteKey =
    (process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA").trim(); // Cloudflare test key
  
  const disabledUntilVerified = !turnstileToken;

  // Submit handler
  async function handleContactSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!cEmail || !cMsg) {
      alert("Please enter your email and message.");
      return;
    }

    if (!turnstileToken) {
      alert("Please verify you are human first.");
      return;
    }

    setCSending(true);
    setCSent(null);

    try {
      console.log("[contact] sending to:", `${API}/api/contact/request`);

      const res = await fetch(`${API}/api/contact/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: cName || null,
          email: cEmail,
          message: cMsg,
          turnstileToken,
        }),
      });

      const text = await res.text();
      console.log("[contact] response status:", res.status);
      console.log("[contact] response body:", text);

      if (!res.ok) {
        // show *actual* server message instead of generic one
        alert(`Contact failed (HTTP ${res.status}): ${text}`);
        setCSent("err");
        return;
      }

      // If we reach here, backend returned 200
      setCSent("ok");
      setCName("");
      setCEmail("");
      setCMsg("");
      setTurnstileToken(null);
    } catch (err: any) {
      console.error("[contact] network error:", err);
      alert("Network error while sending contact: " + (err?.message ?? String(err)));
      setCSent("err");
    } finally {
      setCSending(false);
    }
  }


  React.useEffect(() => {
    if (!contactExpanded) return;

    let cancelled = false;

    function tryRenderTurnstile() {
      const w = window as any;
      if (!turnstileRef.current) return;

      if (w.turnstile && typeof w.turnstile.render === "function") {
        try {
          // clear previous widget (prevents double-render issues)
          console.log("[turnstile] siteKey:", JSON.stringify(siteKey));
          turnstileRef.current.innerHTML = "";

          w.turnstile.render(turnstileRef.current, {
            sitekey: siteKey,
            callback: (token: string) => {
              console.log("[turnstile] token:", token);
              setTurnstileToken(token);
            },
          });
        } catch (e) {
          console.error("[turnstile] render failed:", e);
        }
      } else if (!cancelled) {
        // Script not ready yet – try again in 500ms
        setTimeout(tryRenderTurnstile, 500);
      }
    }

    tryRenderTurnstile();

    return () => {
      cancelled = true;
    };
  }, [contactExpanded, siteKey]);

    React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");

    if (checkout === "success" || checkout === "cancelled") {
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      window.history.replaceState({}, "", url.pathname + url.hash);
    }
  }, []);

    React.useEffect(() => {
    // Called by Turnstile when the widget succeeds
    (window as any).onTurnstileSuccess = (token: string) => {
      console.log("[turnstile] token:", token);
      setTurnstileToken(token);
    };
  }, []);

  React.useEffect(() => {
    const loadDashboardUsage = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;

        if (!token) {
          setAnalysesToday(0);
          return;
        }

        const response = await fetch(`${API}/api/usage`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to load usage (${response.status})`
          );
        }

        const usage = await response.json();

        setAnalysesToday(
          Number(usage.ai ?? 0)
        );
      } catch (error) {
        console.error(
          "Failed to load dashboard usage:",
          error
        );

        setAnalysesToday(0);
      }
    };

    void loadDashboardUsage();

    window.addEventListener("dashboard:usage-changed", loadDashboardUsage);

    return () => {
      window.removeEventListener(
        "dashboard:usage-changed",
        loadDashboardUsage
      );
    };
  }, []);

  React.useEffect(() => {
    const loadRecentAnalyses = () => {
      try {
        if (!user?.id) {
          setRecentAnalyses([]);
          return;
        }

        const storageKey =
          `jobtailor_recent_analyses_${user.id}`;

        const raw = localStorage.getItem(storageKey);
        const parsed = raw ? JSON.parse(raw) : [];

        setRecentAnalyses(
          Array.isArray(parsed) ? parsed : []
        );
      } catch (error) {
        console.error(
          "Failed to load recent analyses:",
          error
        );

        setRecentAnalyses([]);
      }
    };

    loadRecentAnalyses();

    window.addEventListener(
      "dashboard:analyses-changed",
      loadRecentAnalyses
    );

    return () => {
      window.removeEventListener(
        "dashboard:analyses-changed",
        loadRecentAnalyses
      );
    };
  }, [user?.id]);

  React.useEffect(() => {
    const loadCoverLetterCount = () => {
      const count = Number(
        localStorage.getItem("jobtailor_cover_letters_count") ?? "0"
      );

      setCoverLettersCreated(count);
    };

    loadCoverLetterCount();

    window.addEventListener(
      "dashboard:cover-letter-changed",
      loadCoverLetterCount
    );

    return () => {
      window.removeEventListener(
        "dashboard:cover-letter-changed",
        loadCoverLetterCount
      );
    };
  }, []);

  React.useEffect(() => {
    const loadCvCount = () => {
      const count = Number(
        localStorage.getItem("jobtailor_cvs_created_count") ?? "0"
      );

      setCvsCreated(count);
    };

    loadCvCount();

    window.addEventListener("dashboard:cv-created", loadCvCount);

    return () => {
      window.removeEventListener("dashboard:cv-created", loadCvCount);
    };
  }, []);

  React.useEffect(() => {
    // Supabase magic-link often returns: http://localhost:3000/#access_token=...&refresh_token=...
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : "";

    if (!hash) return;

    const params = new URLSearchParams(hash);
    const access_token = params.get("access_token");
    const refresh_token = params.get("refresh_token");
    const error = params.get("error");
    const error_description = params.get("error_description");

    if (error || error_description) {
      console.error("Supabase auth error:", error, error_description);
      alert(error_description || "Sign-in failed.");
    }

    if (access_token && refresh_token) {
      // Persist the session with Supabase
      supabase.auth
        .setSession({ access_token, refresh_token })
        .then(({ data, error }) => {
          if (error) {
            console.error("setSession failed:", error);
          }
          // Clean the hash so your in-app #routes work again
          window.history.replaceState({}, "", window.location.pathname + window.location.search);
        })
        .catch((e) => {
          console.error("setSession exception:", e);
        });
    }
  }, []); 

  React.useEffect(() => {
    const syncFromHash = () => {
      if (window.location.hash === "#contact") {
        setContactExpanded(true);
        setTimeout(() => {
          document.getElementById("contact")?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 0);
      }
    };

    syncFromHash(); // run on initial load
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, []);
 
  const userPlan: UserPlan = plan;
  

  const handleUpgradeClick = () => setShowUpgrade(true);


  const handleChoosePlan = async (plan: "monthly" | "lifetime") => {
    setShowUpgrade(false);

    const startCheckout = async (token: string) => {
      return fetch(`${API}/api/billing/stripe/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan }),
      });
    };

    let token = accessToken;

    if (!token) {
      const { data } = await supabase.auth.getSession();
      token = data.session?.access_token ?? null;
    }

    if (!token) {
      setShowAuth(true);
      return;
    }

    let res = await startCheckout(token);
    let data = await res.json();

    if (!res.ok && (res.status === 401 || String(data?.detail).includes("Invalid token"))) {
      const refreshed = await supabase.auth.refreshSession();
      const newToken = refreshed.data.session?.access_token;

      if (!newToken) {
        setShowAuth(true);
        return;
      }

      res = await startCheckout(newToken);
      data = await res.json();
    }

    if (!res.ok) {
      alert(`Could not start Stripe checkout: ${data?.detail || "Unknown error"}`);
      return;
    }

    if (data.checkout_url) {
      window.location.href = data.checkout_url;
      return;
    }

    alert("Stripe did not return a checkout URL.");
  };

  const initials = (() => {
    const name =
      user?.user_metadata?.full_name ||
      user?.user_metadata?.name ||
      user?.email ||
      "";

    const parts = name.trim().split(/\s+/);

    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }

    return name.charAt(0).toUpperCase();
  })();

  const handleAtsCvUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const form = new FormData();
      form.append("file", file);

      const response = await fetch(`${API}/parse-resume`, {
        method: "POST",
        body: form,
      });

      const raw = await response.text();

      if (!response.ok) {
        throw new Error(
          `Could not read CV (${response.status}): ${raw}`
        );
      }

      const data = JSON.parse(raw);

      setAtsCvText(data.text || "");
    } catch (error) {
      console.error("[ATS CV upload] error:", error);

      setAtsError(
        error instanceof Error
          ? error.message
          : "Could not read the CV."
      );
    }
  };

  const handleCheckAts = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    setAtsError("");

    if (!atsCvText.trim()) {
      setAtsError("Please upload or paste your CV first.");
      return;
    }

    if (!atsJobDescription.trim()) {
      setAtsError("Please paste the job description first.");
      return;
    }

    try {
      setAtsLoading(true);

      const { data: sessionData } =
        await supabase.auth.getSession();

      const token =
        sessionData.session?.access_token;

      if (!token) {
        setShowAuth(true);
        return;
      }

      const response = await fetch(`${API}/analyze`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          job_description: atsJobDescription,
          resume_text: atsCvText,
        }),
      });

      const raw = await response.text();

      if (response.status === 429) {
        setShowUpgrade(true);
        return;
      }

      if (response.status === 401) {
        setShowAuth(true);
        return;
      }

      if (!response.ok) {
        throw new Error(
          `ATS check failed (${response.status}): ${raw}`
        );
      }

      const data = JSON.parse(raw);

      setAtsReport(data);

      setAtsMatch(data.match_score ?? 0);

      window.dispatchEvent(
        new Event("dashboard:usage-changed")
      );
    } catch (error) {
      console.error("[ATS Checker] error:", error);

      setAtsError(
        error instanceof Error
          ? error.message
          : "Could not check ATS compatibility."
      );
    } finally {
      setAtsLoading(false);
    }
  };

  return (
    <div
      className={
        themeMode === "dark"
          ? "jobtailor-theme-dark min-h-screen"
          : "jobtailor-theme-light min-h-screen"
      }
    >
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        onCheckout={handleChoosePlan}
        plan={plan}
      />

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />

      <div className="flex">
        {/* Sidebar */}
        <DashboardSidebar
          activeView={activeView}
          onChangeView={(view) => {
            const protectedViews = [
              "job-analyzer",
              "resume-analyzer",
              "cv-builder",
              "cover-letter",
              "ats-checker",
              "saved-documents",
              "applications",
              "settings",
            ];

            if (protectedViews.includes(view)) {
              if (!user) {
                setShowAuth(true);
                return;
              }

              if (
                view === "ats-checker" ||
                view === "saved-documents" ||
                view === "applications" ||
                view === "settings"
              ) {
                setActiveView(view);
                return;
              }

              setActiveView("dashboard");

              setInitialWorkspace(
                view as
                  | "job-analyzer"
                  | "resume-analyzer"
                  | "cv-builder"
                  | "cover-letter"
              );

              return;
            }

            if (view === "dashboard") {
              setInitialWorkspace("none");
              setActiveView("dashboard");
              return;
            }

            setActiveView(view);
          }}
          onUpgrade={handleUpgradeClick}
        />

            {/* =========================================
                MOBILE MENU — MOBILE ONLY
              ========================================= */}

            <div className="md:hidden">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="dashboard-mobile-menu__button"
                aria-expanded={menuOpen}
                aria-label="Open dashboard menu"
              >
                Menu ☰
              </button>

              {menuOpen && (
                <nav className="dashboard-mobile-menu">
                  {/* Dashboard */}
                  <button
                    type="button"
                    onClick={() => {
                      setActiveView("dashboard");
                      setInitialWorkspace("none");
                      setMenuOpen(false);
                    }}
                  >
                    Dashboard
                  </button>

                  {/* Job Analyzer */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        setShowAuth(true);
                        setMenuOpen(false);
                        return;
                      }

                      setActiveView("dashboard");
                      setInitialWorkspace("job-analyzer");
                      setMenuOpen(false);
                    }}
                  >
                    Job Analyzer
                  </button>

                  {/* CV Analyzer */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        setShowAuth(true);
                        setMenuOpen(false);
                        return;
                      }

                      setActiveView("dashboard");
                      setInitialWorkspace("resume-analyzer");
                      setMenuOpen(false);
                    }}
                  >
                    CV Analyzer
                  </button>

                  {/* CV Builder */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        setShowAuth(true);
                        setMenuOpen(false);
                        return;
                      }

                      setActiveView("dashboard");
                      setInitialWorkspace("cv-builder");
                      setMenuOpen(false);
                    }}
                  >
                    CV Builder
                  </button>

                  {/* Cover Letter */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        setShowAuth(true);
                        setMenuOpen(false);
                        return;
                      }

                      setActiveView("dashboard");
                      setInitialWorkspace("cover-letter");
                      setMenuOpen(false);
                    }}
                  >
                    Cover Letter
                  </button>

                  {/* ATS Checker */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        setShowAuth(true);
                        setMenuOpen(false);
                        return;
                      }

                      setActiveView("ats-checker");
                      setMenuOpen(false);
                    }}
                  >
                    ATS Checker
                  </button>

                  {/* Saved Documents */}
                  <button
                    type="button"
                    onClick={() => {
                      setActiveView("saved-documents");
                      setMenuOpen(false);
                    }}
                  >
                    Saved Documents
                  </button>

                  {/* Applications */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        setShowAuth(true);
                        setMenuOpen(false);
                        return;
                      }

                      setActiveView("applications");
                      setMenuOpen(false);
                    }}
                  >
                    Applications
                  </button>

                  {/* Settings */}
                  <button
                    type="button"
                    onClick={() => {
                      setActiveView("settings");
                      setMenuOpen(false);
                    }}
                  >
                    Settings
                  </button>
                </nav>
              )}
            </div>
                 
        {/* Main */}
        <main className="flex-1 min-h-screen bg-[#f6f7fb]">
          {/* Header */}
          <DashboardHeader
            userEmail={user?.email}
            initials={initials}
            plan={plan}
            language={uiLanguage}
            onChangeLanguage={setUiLanguage}
            onUpgrade={handleUpgradeClick}
            onSignIn={() => setShowAuth(true)}
            onSignOut={() => void signOut()}
            onOpenMobileMenu={() => setMenuOpen((open) => !open)}
          />

          {/* Content */}
          <div className="w-full mx-auto max-w-6xl p-4 md:p-6">
            {activeView === "dashboard" ? (
              <DashboardHome
                isSignedIn={!!user}
                userPlan={userPlan}
                userId={user?.id ?? null}
                onHitPaywall={() => setShowUpgrade(true)}
                initialWorkspace={initialWorkspace}
                jobsAnalyzed={recentAnalyses.length}
                coverLettersCreated={coverLettersCreated}
                cvsCreated={cvsCreated}
                recentAnalyses={recentAnalyses}
                resumeScore={resumeScore}
                onAtsMatchChange={setAtsMatch}
                onResumeScoreChange={setResumeScore}
                atsMatch={atsMatch}

                onCvCreated={() => {
                  setCvsCreated((prev) => prev + 1);
                }}
                onCoverLetterCreated={() => {
                  setCoverLettersCreated((prev) => prev + 1);
                }}
                onRequireSignIn={() => setShowAuth(true)}
                onOpenJobAnalyzer={() => {
                  if (!user) {
                    setShowAuth(true);
                    return;
                  }

                  setInitialWorkspace("job-analyzer");
                  setActiveView("dashboard");
                }}

                onOpenResumeAnalyzer={() => {
                  if (!user) {
                    setShowAuth(true);
                    return;
                  }

                  setInitialWorkspace("resume-analyzer");
                  setActiveView("dashboard");
                }}

                onOpenCVBuilder={() => {
                  if (!user) {
                    setShowAuth(true);
                    return;
                  }

                  setInitialWorkspace("cv-builder");
                  setActiveView("dashboard");
                }}

                onOpenCoverLetter={() => {
                  if (!user) {
                    setShowAuth(true);
                    return;
                  }

                  setInitialWorkspace("cover-letter");
                  setActiveView("dashboard");
                }}
              />
            ) : activeView === "job-analyzer" ? (
              <DashboardHome
                isSignedIn={!!user}
                userPlan={userPlan}
                userId={user?.id ?? null}
                onHitPaywall={() => setShowUpgrade(true)}
                initialWorkspace={initialWorkspace}
                jobsAnalyzed={recentAnalyses.length}
                coverLettersCreated={coverLettersCreated}
                cvsCreated={cvsCreated}
                recentAnalyses={recentAnalyses}
                onAtsMatchChange={setAtsMatch}
                onResumeScoreChange={setResumeScore}
                resumeScore={resumeScore}
                atsMatch={atsMatch}

                onCvCreated={() => {
                  setCvsCreated((prev) => prev + 1);
                }}
                onCoverLetterCreated={() => {
                  setCoverLettersCreated((prev) => prev + 1);
                }}

                onRequireSignIn={() => setShowAuth(true)}

                onOpenJobAnalyzer={() => {
                  if (!user) {
                    setShowAuth(true);
                    return;
                  }

                  setInitialWorkspace("job-analyzer");
                  setActiveView("dashboard");
                }}

                onOpenResumeAnalyzer={() => {
                  if (!user) {
                    setShowAuth(true);
                    return;
                  }

                  setInitialWorkspace("resume-analyzer");
                  setActiveView("dashboard");
                }}

                onOpenCVBuilder={() => {
                  if (!user) {
                    setShowAuth(true);
                    return;
                  }

                  setInitialWorkspace("cv-builder");
                  setActiveView("dashboard");
                }}

                onOpenCoverLetter={() => {
                  if (!user) {
                    setShowAuth(true);
                    return;
                  }

                  setInitialWorkspace("cover-letter");
                  setActiveView("dashboard");
                }}
                />         
          ) : activeView === "resume-analyzer" ? (
            <section id="resume-analyzer" className="space-y-4">
                <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-primary">
                    Resume optimization
                </p>

                <h1 className="mt-2 text-3xl font-semibold text-gray-900 dark:text-gray-100">
                    Resume Analyzer
                </h1>

                <p className="mt-2 text-subtext">
                    Compare your resume with a job description and receive targeted,
                    ATS-friendly improvement suggestions.
                </p>
                </div>

                <div className="rounded-2xl border border-border bg-panel p-4 md:p-6">
                <Analyzer
                    userPlan={userPlan}
                    onHitPaywall={() => setShowUpgrade(true)}
                />
                </div>
            </section>
            ) : activeView === "cover-letter" ? (
              <section id="cover-letter" className="space-y-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wider text-primary">
                    AI application writing
                  </p>

                  <h1 className="mt-2 text-3xl font-semibold text-gray-900 dark:text-gray-100">
                    Cover Letter
                  </h1>

                  <p className="mt-2 text-subtext">
                    Generate a tailored cover letter using the job description and your
                    professional background.
                  </p>
                </div>

                <div className="rounded-2xl border border-border bg-panel p-4 md:p-6">
                  <Analyzer
                    userPlan={userPlan}
                    onHitPaywall={() => setShowUpgrade(true)}
                  />
                </div>
              </section>

            ) : activeView === "ats-checker" ? (
              <section id="ats-checker" className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-primary">
                      ATS compatibility
                    </p>

                    <h1 className="mt-2 text-3xl font-semibold text-gray-900">
                      ATS Checker
                    </h1>

                    <p className="mt-2 text-subtext">
                      Check how well your CV is optimized for applicant tracking systems
                      and compare it with a job description for keyword and structure fit.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setActiveView("dashboard");
                    }}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-2xl text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
                    aria-label="Close ATS Checker"
                    title="Back to dashboard"
                  >
                    ×
                  </button>
                </div>

                <div className="rounded-2xl border border-border bg-white p-4 md:p-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* LEFT */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Your CV
                      </h3>

                      <textarea
                        className="mt-3 min-h-[320px] w-full rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-900 outline-none"
                        placeholder="Paste your CV text here..."
                        value={atsCvText}
                        onChange={(e) => setAtsCvText(e.target.value)}
                      />

                      <div className="my-4 text-center text-xs font-semibold text-gray-400">
                        OR
                      </div>

                      <label className="flex cursor-pointer items-center justify-center rounded-xl border border-dashed border-violet-300 bg-violet-50 px-4 py-5 text-sm font-semibold text-violet-700">
                        Upload CV
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          hidden
                          onChange={handleAtsCvUpload}
                        />
                      </label>
                    </div>

                    {/* RIGHT */}
                    <div>
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Job Description
                        </h3>

                        {atsJobDescription && (
                          <button
                            type="button"
                            onClick={() => {
                              setAtsJobDescription("");
                              setAtsMatch(0);
                              setAtsError("");
                            }}
                            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-xl text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
                            aria-label="Clear job description"
                            title="Clear job description"
                          >
                            ×
                          </button>
                        )}
                      </div>

                      <textarea
                        value={atsJobDescription}
                        onChange={(e) => setAtsJobDescription(e.target.value)}
                        className="mt-3 min-h-[320px] w-full rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-900 outline-none"
                        placeholder="Paste the job description here..."
                      />

                      <button
                        type="button"
                        onClick={() => void handleCheckAts()}
                        disabled={atsLoading}
                        className="mt-4 w-full rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 px-4 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {atsLoading ? "Checking..." : "Check ATS Score"}
                      </button>
                    </div>

                    {/* ATS RESULT */}
                    <div className="mt-6 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-8">
                      {atsReport ? (
                        <div className="space-y-5">
                          {/* SCORE */}
                          <div>
                            <span className="text-sm font-semibold uppercase tracking-wider text-violet-600">
                              ATS Match
                            </span>

                            <div className="mt-1 text-4xl font-bold text-gray-900">
                              {atsReport.match_score ?? 0}%
                            </div>
                          </div>

                          {/* MATCHED KEYWORDS */}
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Matched Keywords
                            </h4>

                            <p className="mt-2 text-sm text-gray-600">
                              {Array.isArray(atsReport.top_common_keywords) &&
                              atsReport.top_common_keywords.length > 0
                                ? atsReport.top_common_keywords.join(", ")
                                : "No strong keyword matches detected."}
                            </p>
                          </div>

                          {/* MISSING KEYWORDS */}
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Missing Keywords
                            </h4>

                            <p className="mt-2 text-sm text-gray-600">
                              {Array.isArray(atsReport.missing_keywords) &&
                              atsReport.missing_keywords.length > 0
                                ? atsReport.missing_keywords.join(", ")
                                : "No important missing keywords detected."}
                            </p>
                          </div>

                          {/* SUGGESTIONS */}
                          {Array.isArray(atsReport.suggestions) &&
                            atsReport.suggestions.length > 0 && (
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  Recommended Improvements
                                </h4>

                                <div className="mt-3 space-y-4">
                                  {atsReport.suggestions.map(
                                    (suggestion: any, index: number) => (
                                      <div
                                        key={index}
                                        className="rounded-xl border border-gray-200 bg-white p-4"
                                      >
                                        <p className="text-xs font-semibold uppercase text-gray-400">
                                          Current
                                        </p>

                                        <p className="mt-1 text-sm text-gray-700">
                                          {suggestion.original}
                                        </p>

                                        <p className="mt-3 text-xs font-semibold uppercase text-violet-600">
                                          Suggested
                                        </p>

                                        <p className="mt-1 text-sm text-gray-700">
                                          {suggestion.rewrite}
                                        </p>

                                        {suggestion.reason && (
                                          <p className="mt-2 text-xs text-gray-500">
                                            {suggestion.reason}
                                          </p>
                                        )}
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      ) : (
                        <div className="text-center">
                          <strong className="text-gray-900">
                            Your ATS report will appear here
                          </strong>

                          <p className="mt-2 text-sm text-gray-500">
                            Upload or paste your CV and add a job description, then click
                            Check ATS Score.
                          </p>
                        </div>
                      )}

                      {atsError && (
                      <p className="mt-4 text-center text-sm font-medium text-red-600">
                        {atsError}
                      </p>
                    )}
                  </div>
                </div>

                {/* close rounded ATS card */}
              </div>
            </section>

            ) : activeView === "saved-documents" ? (
              <section id="saved-documents" className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-primary">
                      Saved items
                    </p>

                    <h1 className="mt-2 text-3xl font-semibold text-gray-900">
                      Saved Documents
                    </h1>

                    <p className="mt-2 text-subtext">
                      Access the jobs and application items you have saved in JobTailorAI.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveView("dashboard")}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-2xl text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
                    aria-label="Close Saved Documents"
                    title="Back to dashboard"
                  >
                    ×
                  </button>
                </div>

                <SavedJobs />
              </section>

            ) : activeView === "applications" ? (
              <section id="applications" className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-primary">
                      Application tracker
                    </p>

                    <h1 className="mt-2 text-3xl font-semibold text-gray-900">
                      Applications
                    </h1>

                    <p className="mt-2 text-subtext">
                      Track the jobs you have applied to and follow their progress
                      from application to interview and offer.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveView("dashboard")}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-2xl text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
                    aria-label="Close Applications"
                    title="Back to dashboard"
                  >
                    ×
                  </button>
                </div>

                <Applications />
              </section>
            ) : activeView === "settings" ? (
              <section id="settings" className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-primary">
                      Account preferences
                    </p>

                    <h1 className="mt-2 text-3xl font-semibold text-gray-900">
                      Settings
                    </h1>

                    <p className="mt-2 text-subtext">
                      Manage your account, plan, language, and session preferences.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveView("dashboard")}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-2xl text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
                    aria-label="Close Settings"
                    title="Back to dashboard"
                  >
                    ×
                  </button>
                </div>

                <Settings
                  email={user?.email}
                  plan={userPlan}
                  language={uiLanguage}
                  onChangeLanguage={setUiLanguage}
                  theme={themeMode}
                  onChangeTheme={setThemeMode}
                  onUpgrade={handleUpgradeClick}
                  onSignOut={() => void signOut()}
                />
              </section>

            ) : (
              <div className="rounded-2xl border border-border bg-panel p-8">
                <h1 className="text-2xl font-semibold text-gray-900 capitalize">
                  {activeView.replaceAll("-", " ")}
                </h1>

                <p className="mt-3 text-subtext">
                  This section will be connected next.
                </p>
              </div>
            )}

        </div>
      </main>
  </div>
</div>
);
}